import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import atentionIcon from '../../../images/pcmc-attention-lines-mobile.svg';
import Container from '../../Core/Container';
import Link from '../../Core/Link';
import RichText, { fragment } from '../../Core/RichText';
import IndividualBlade from '../IndividualBlades';

import TabList from './TabList';
import ChartRow from './ChartRow';
import {
  TabPanel,
  ChartWrapper,
  ChartHeading,
  ChartIntro,
  Caption,
  THead,
  TBody,
  Footer,
  Footnote,
  StyledImageContainer,
  StyledImage,
} from './styles';

const Chart = ({ data = null }) => {
  // * Null check for data
  if (!data) return null;

  // * Destructure data values
  const {
    component,
    anchorName,
    className,
    textAlignment,
    heading,
    bodyCopy,
    caption,
    rows,
    legal,
    ctas,
  } = data;

  const headers = data.rows[0].cells
    .reduce((acc, item) => {
      return item.imageDam
        ? `${acc} ${item.imageDam.altText.toLowerCase().replace(/\s/g, '-')}`
        : acc;
    }, '')
    .trim();

  const firstSelectableCell = data.rows[0].cells
    .map(cell => cell.selectable)
    .indexOf(true);

  // * State to track which card is actively selected
  const [activeColumnIdx, setActiveColumnIdx] = useState(firstSelectableCell);

  const tabIdArr = data.rows[0].cells
    .filter(cell => cell.selectable)
    .map(cell => {
      return `tab-${cell.id}`;
    });

  const idArr = tabIdArr.join(' ');

  const [hasWindow, setHasWindow] = useState(false);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setHasWindow(true);
    }
  }, []);

  return (
    <>
      {hasWindow && (
        <Container
          as="section"
          className={`chartArea ${component}`}
          id={anchorName}
          // backgroundColor={bgColor}
        >
          <>
            {heading && (
              <ChartHeading
                className={`${
                  textAlignment || 'left'
                } ${component} ${className}`}
              >
                {className === 'pcmc-whisker' ? (
                  <StyledImageContainer>
                    <StyledImage src={atentionIcon} alt="" />
                  </StyledImageContainer>
                ) : null}
                <RichText document={heading} />
              </ChartHeading>
            )}

            {bodyCopy && (
              <ChartIntro className={`${textAlignment || 'left'}`}>
                <RichText document={bodyCopy} />
              </ChartIntro>
            )}
          </>
          {component.includes('selectable-card chart') ? (
            <>
              {rows.map(
                row =>
                  row.isSticky && (
                    <TabList
                      key={row.id}
                      columnCount={row.columnCount}
                      data={row.cells}
                      isSticky
                      className={component}
                      activeColumnIdx={activeColumnIdx}
                      firstSelectableCell={firstSelectableCell}
                      setActiveColumn={setActiveColumnIdx}
                    />
                  ),
              )}
              <TabPanel
                tabIndex="0"
                role="tabpanel"
                id="chart-tab"
                aria-labelledby={idArr}
                className="tabContent"
              >
                <ChartWrapper className={component}>
                  {caption && (
                    <Caption className="sr-only">
                      <RichText document={caption} />
                    </Caption>
                  )}
                  {rows.map(
                    row =>
                      row.isSticky && (
                        <THead key={row.id}>
                          <ChartRow
                            key={row.id}
                            component={row.component}
                            componentStyle={row.componentStyle}
                            columnCount={row.columnCount}
                            data={row.cells}
                            className={`sr-only ${component}`}
                            isHeader
                            isSticky
                            activeColumnIdx={activeColumnIdx}
                            firstSelectableCell={firstSelectableCell}
                            setActiveColumn={setActiveColumnIdx}
                          />
                        </THead>
                      ),
                  )}
                  <TBody role="rowgroup">
                    {rows.map(
                      row =>
                        !row.isSticky && (
                          <ChartRow
                            key={row.id}
                            component={row.component}
                            componentStyle={row.componentStyle}
                            columnCount={row.columnCount}
                            data={row.cells}
                            className={component}
                            headers={headers}
                            activeColumnIdx={activeColumnIdx}
                            firstSelectableCell={firstSelectableCell}
                          />
                        ),
                    )}
                  </TBody>
                </ChartWrapper>
              </TabPanel>
            </>
          ) : (
            <ChartWrapper className={component}>
              {caption && (
                <Caption className="sr-only">
                  <RichText document={caption} />
                </Caption>
              )}
              {rows.map(
                row =>
                  row.isSticky && (
                    <THead key={row.id}>
                      <ChartRow
                        key={row.id}
                        component={row.component}
                        componentStyle={row.componentStyle}
                        columnCount={row.columnCount}
                        data={row.cells}
                        className={component}
                        isHeader
                        isSticky
                        activeColumnIdx={activeColumnIdx}
                        firstSelectableCell={firstSelectableCell}
                        setActiveColumn={setActiveColumnIdx}
                      />
                    </THead>
                  ),
              )}
              <TBody role="rowgroup">
                {rows.map(
                  row =>
                    !row.isSticky && (
                      <ChartRow
                        key={row.id}
                        component={row.component}
                        componentStyle={row.componentStyle}
                        columnCount={row.columnCount}
                        data={row.cells}
                        className={component}
                        headers={headers}
                        activeColumnIdx={activeColumnIdx}
                        firstSelectableCell={firstSelectableCell}
                      />
                    ),
                )}
              </TBody>
            </ChartWrapper>
          )}
          {legal && (
            <Footnote columnCount={rows[0].columnCount} className={component}>
              <RichText document={legal} />
            </Footnote>
          )}
          {ctas && (
            <Footer>
              {ctas.map(cta => {
                if (cta.hideFrench === true) return null;
                return cta.internal.type === 'ContentfulBlade' ? (
                  <IndividualBlade key={cta.id} data={cta} />
                ) : (
                  <Link key={cta.id} data={cta}>
                    <RichText document={cta.label} options={fragment} />
                  </Link>
                );
              })}
            </Footer>
          )}
        </Container>
      )}
    </>
  );
};

Chart.propTypes = {
  data: PropTypes.shape(),
};

export default Chart;
